import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Pill, MapPin, Phone, Moon, Navigation, Calendar, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fiche publique d'une pharmacie — volontairement un fichier DEDIE plutot
// qu'une branche de plus dans ClinicProfile.js (deja tres charge de
// ternaires isHospital/isLab pour gerer medecins/services/reservation/avis,
// dont RIEN ne s'applique a une pharmacie de ville). Meme lecon que la
// reconstruction du systeme labo : ne pas preter a un etablissement la
// logique concue pour un autre. Reutilise le meme GET /api/clinics/{slug}
// (deja etablissement-agnostique, routes/clinics.py), qui renvoie en plus
// is_garde_today/garde_dates quand establishment_type === "pharmacy".
//
// Horaires EN DIRECT (2026-09-05) : appel separe a GET
// /api/clinics/{slug}/live-info (backend/google_places.py) pour le
// telephone/les horaires/le statut ouvert-ferme, via Google Places au
// moment ou la fiche est ouverte — jamais stocke durablement cote site
// (cache court cote backend uniquement, quelques dizaines de minutes),
// conformement aux conditions Google Maps Platform. Volontairement une
// requete SEPAREE de celle qui charge le reste de la fiche (nom/adresse/
// garde) : un Google Places lent ou indisponible ne doit jamais empecher
// ni retarder l'affichage du reste de la page.
const PharmacyProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Horaires/telephone EN DIRECT (Google Places, backend/google_places.py) —
  // requete separee de la fiche principale, volontairement : un Google
  // Places lent ou en panne ne doit jamais retarder ni casser l'affichage
  // du reste de la fiche (nom/adresse/garde, deja charges par ailleurs).
  // Rien de tout ca n'est stocke cote site au-dela d'un cache court cote
  // backend — cette page se contente d'afficher ce que l'API renvoie a
  // l'instant T.
  const [liveInfo, setLiveInfo] = useState(null);
  const [liveInfoLoading, setLiveInfoLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API_URL}/api/clinics/${slug}`)
      .then((res) => {
        if (res.data?.clinic?.establishment_type !== 'pharmacy') {
          setNotFound(true);
          return;
        }
        setClinic(res.data.clinic);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    setLiveInfo(null);
    setLiveInfoLoading(true);
    axios.get(`${API_URL}/api/clinics/${slug}/live-info`)
      .then((res) => setLiveInfo(res.data))
      .catch(() => setLiveInfo({ available: false }))
      .finally(() => setLiveInfoLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin w-12 h-12 border-4 border-[#0596DE] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (notFound || !clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <Pill className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Pharmacie introuvable</h1>
          <p className="text-[#64748B] mb-6">Cette pharmacie n'existe pas ou n'est plus active.</p>
          <Button onClick={() => navigate('/pharmacies')} className="rounded-full">Voir les pharmacies</Button>
        </div>
      </div>
    );
  }

  const mapQuery = encodeURIComponent(`${clinic.name}, ${clinic.address || ''}, ${clinic.daira || clinic.commune || ''}, Algerie`);
  const gardeDates = clinic.garde_dates || [];

  // Constat Nadjib (2026-09-06) : plusieurs fiches affichaient la mention
  // "Horaires fournis par Google, a titre indicatif." toute seule, sans
  // aucune donnee au-dessus (ni statut ouvert/ferme, ni liste de jours, ni
  // telephone) — Google renvoie parfois "available: true" (un lieu a bien
  // ete trouve) mais sans opening_hours ni telephone exploitables, ce qui
  // donnait l'impression d'une fiche cassee. On ne considere donc plus
  // liveInfo.available seul comme suffisant : il faut au moins UNE info
  // concrete a afficher pour montrer le bloc "rempli" (sinon on retombe sur
  // le meme message "non disponible" que pour une pharmacie introuvable sur
  // Google).
  //
  // CORRECTIF GENERALISE (2026-09-06, constat Nadjib sur pharmacie-bouchakour-
  // nada — corrige une bonne fois pour toutes les fiches concernees, pas une
  // par une) : quand Google ne renvoie NI weekday_text NI open_now mais UN
  // telephone, l'ancienne logique traitait quand meme ce cas comme "des
  // horaires disponibles" et affichait le telephone seul sous le titre
  // "Horaires" avec la mention "Horaires fournis par Google, a titre
  // indicatif." — alors qu'aucune heure n'est reellement affichee, ce qui
  // est trompeur. On distingue maintenant strictement les "vraies horaires"
  // (weekday_text ou open_now) du simple "telephone de secours", avec un
  // message et un disclaimer distincts pour ce dernier cas.
  const hasRealHours = !!(
    liveInfo?.available && (
      liveInfo.weekday_text?.length > 0 ||
      (liveInfo.open_now !== null && liveInfo.open_now !== undefined)
    )
  );
  const hasLivePhoneOnly = !!(
    !hasRealHours && liveInfo?.available && !clinic.phone && liveInfo.phone
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <div className="bg-gradient-to-r from-[#0596DE] to-[#047CBA] py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 shrink-0 rounded-2xl bg-white/15 flex items-center justify-center">
              <Pill className="w-8 h-8 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-bold text-white">{clinic.name}</h1>
                {clinic.is_garde_today && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-[#0596DE] text-xs font-semibold">
                    <Moon className="w-3.5 h-3.5" /> De garde aujourd'hui
                  </span>
                )}
              </div>
              <p className="text-white/80 mt-1">Pharmacie</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
          <h2 className="text-lg font-bold text-[#0F172A] mb-4">Coordonnées</h2>
          <div className="space-y-3 text-[#334155]">
            {/* Adresse : ~71% des 2390 pharmacies importees (fichier source)
                n'ont pas de champ adresse renseigne (seulement nom+wilaya+GPS)
                — cas constate par Nadjib sur "Bab Ezzouar City Universite"
                (2026-09-06). Plutot que de laisser la carte vide, on retombe
                sur `formatted_address` de Google (deja recupere par le fetch
                live existant pour les horaires, aucun appel Google
                supplementaire) quand la fiche elle-meme n'a pas d'adresse. */}
            {clinic.address ? (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
                <span>{clinic.address}{clinic.daira ? `, ${clinic.daira}` : clinic.commune ? `, ${clinic.commune}` : ''}</span>
              </div>
            ) : liveInfoLoading ? (
              <div className="flex items-start gap-3 animate-pulse">
                <MapPin className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
                <div className="h-4 bg-gray-200 rounded w-2/3 mt-1" />
              </div>
            ) : liveInfo?.formatted_address ? (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
                <div>
                  <span>{liveInfo.formatted_address}</span>
                  <p className="text-xs text-[#94A3B8] mt-0.5">Adresse fournie par Google, à titre indicatif.</p>
                </div>
              </div>
            ) : null}
            {clinic.phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#0596DE] shrink-0" />
                <a href={`tel:${clinic.phone}`} className="hover:text-[#0596DE]">{clinic.phone}</a>
              </div>
            )}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#0596DE] text-sm font-medium hover:underline"
            >
              <Navigation className="w-4 h-4" /> Itinéraire
            </a>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
          <h2 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#0596DE]" /> Horaires
          </h2>
          {liveInfoLoading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/3" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          ) : hasRealHours ? (
            <div className="space-y-3">
              {liveInfo.open_now !== null && liveInfo.open_now !== undefined && (
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    liveInfo.open_now ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${liveInfo.open_now ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  {liveInfo.open_now ? 'Ouvert maintenant' : 'Fermé actuellement'}
                </span>
              )}
              {liveInfo.weekday_text?.length > 0 && (
                <ul className="text-sm text-[#334155] space-y-1">
                  {liveInfo.weekday_text.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              {/* N'affiche le telephone ici que si la fiche n'en a pas deja un
                  (voir bloc "Coordonnées" ci-dessus) — evite un doublon visuel
                  pour les rares pharmacies ayant deja un telephone en base. */}
              {!clinic.phone && liveInfo.phone && (
                <div className="flex items-center gap-3 pt-1">
                  <Phone className="w-5 h-5 text-[#0596DE] shrink-0" />
                  <a href={`tel:${liveInfo.phone}`} className="hover:text-[#0596DE]">{liveInfo.phone}</a>
                </div>
              )}
              <p className="text-xs text-[#94A3B8]">Horaires fournis par Google, à titre indicatif.</p>
            </div>
          ) : hasLivePhoneOnly ? (
            <div className="space-y-3">
              <p className="text-[#64748B] text-sm">Horaires non disponibles pour cette pharmacie pour le moment.</p>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#0596DE] shrink-0" />
                <a href={`tel:${liveInfo.phone}`} className="hover:text-[#0596DE]">{liveInfo.phone}</a>
              </div>
              <p className="text-xs text-[#94A3B8]">Téléphone fourni par Google, à titre indicatif.</p>
            </div>
          ) : (
            <p className="text-[#64748B] text-sm">Horaires non disponibles pour cette pharmacie pour le moment.</p>
          )}
        </div>

        {clinic.description && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">À propos</h2>
            <p className="text-[#334155] whitespace-pre-line">{clinic.description}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
          <h2 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#0596DE]" /> Jours de garde à venir
          </h2>
          {gardeDates.length === 0 ? (
            <p className="text-[#64748B] text-sm">Aucun jour de garde déclaré pour le moment.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {gardeDates.map((g) => (
                <span key={g.date} className="px-3 py-1.5 rounded-lg bg-[#E0F2FE] text-[#0596DE] text-sm font-medium">
                  {new Date(g.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PharmacyProfile;
